// What kinds of transport each city has, as data: the MODE REGISTRY.
//
// IMPORT-FREE, like `src/city.js`, `src/rules.js` and `src/severity.js`, and for
// the same reason: the server's push sweep reads it, the build scripts read it,
// the first paint reads it, and none of them may acquire Vue or a bundle on the
// way. Everything below is a plain object and a pure function over it.
//
// ── WHY THIS FILE EXISTS ────────────────────────────────────────────────────
//
// Melbourne's trams were built first, and they were built as "the trams": a
// 6000+ id band spelled in four places, an `isTramLine` in six, a `tramsOn`
// switch, an "Any trams?" step, a TRAINS | TRAMS switch, a tab called BOARD
// when trams are on, push titles that say "route", and a severity rulebook with
// the word in its regexes. Every one of those is a fact about a MODE, not about
// Melbourne's trams, and a second mode in any state (a ferry, a bus, Perth's
// next light rail) would have meant copying each of them.
//
// So a city DECLARES its modes below, and the generic parts of the app read
// the declaration instead of the word. Adding a mode is a declaration, a data
// file, a server adapter and nothing else — see "WHAT A NEW MODE NEEDS" at the
// foot of this comment.
//
// ── THE SHAPE ───────────────────────────────────────────────────────────────
//
// `CITY_MODES[city][modeId]` is one mode, and `modeId` is the string every
// stored row, follow leg and wire message already carries (`mode: 'tram'`,
// `kind: 'tram'`), so a declaration never renames anything that is already on
// somebody's phone. Trains are the base network and are not declared: a mode
// here is something a city has IN ADDITION to its trains.
//
//   source     'adapter'  its own stops, ids, boards and feed (Melbourne's
//                         trams: PTV's tram stops are not stations).
//              'network'  lines inside the city's train bundle, tagged with
//                         `mode` there (Sydney's L1–L4). Its stops ARE stations,
//                         so it has no board of its own and nothing to opt in to.
//   adapter    (adapter) the server adapter's name; see `server/modes.mjs`.
//   api        (adapter) the URL prefix the client asks. Stable on the wire:
//              Melbourne's is `/api/trams`, which is what every deployed build
//              already calls.
//   band       (adapter) `{ base, max }`: a line id is `base + route number`.
//              Every line id in the app lives in ONE namespace (the push sweep
//              is city-blind), so bands may never overlap each other or any
//              city's own ids; `assertBands` checks it and a test runs it.
//   data       (adapter) the lazily-loaded files, by name under src/data/. The
//              entry chunk may import none of them (tests/entry-chunk.test.js
//              reads this list), and `src/mode-data.js` holds the loaders.
//   labels     the words: what one is called, what a line of it is called, a
//              stop of it, its tag on a search row, its heading on Choose lines.
//   badge      how a row of it draws: 'number' (a coloured badge with the route
//              number) or 'bar' (the train ledger's colour bar).
//   severity   which rulebook judges its notices: 'rail' (the trains') or
//              'street' (runs on the road: a short hold-up is not news), and
//              the delay in minutes past which a street mode alerts.
//   optIn      off until a person turns it on. Opt-in modes have a welcome
//              step, a Settings row and a one-time card; the rest are simply on.
//   hidden     the mode is fully declared, has data and a server adapter, and is
//              kept OFF every screen: `modesOf`, `adapterModes`, `optInModes` and
//              `boardModes` (what the UI walks) skip it. `allModes`,
//              `everyAdapterMode`, `modeOf`, `modeOfLine` and `modeOfRow` (what
//              the server, the build scripts and the push sweep walk) still see
//              it, so its line ids classify and its `/api` routes answer. A mode
//              ships in two steps — data and server first, UI second — and this
//              is the seam between them; flipping it is the first line of the UI PR.
//   pairing    how the two poles of one stop are paired: 'number' (the sign's
//              number, the default: trams) or 'road' (the road after the "/" in
//              the name, for a mode whose stops carry no number: buses).
//   prefs      the storage keys. Tram's are the keys it has always written, so
//              nothing is migrated; a new mode gets the default pattern.
//   welcome / settings / intro / home   the copy for the opt-in surfaces.
//   planner    the trip planner's switch and endpoint, or null.
//   windowMinutes  how many minutes ahead a stop's board looks (server and copy).
//   stopFacts  badges for a stop that are the mode's own (Melbourne's Free Tram
//              Zone), as `{ field, label, atBothEnds }`.
//   search     how its stop numbers are typed and printed.
//   credits    the footnote under its route screen.
//
// ── WHAT A NEW MODE NEEDS ───────────────────────────────────────────────────
//
//   1. A declaration below, with a band nobody else has.
//   2. A data file `src/data/<name>.<city>.json` (the routes and stops) and one
//      line in `src/mode-data.js` loading it.
//   3. A server adapter exporting `route`, `sweepRows` and `fetchRun`, and one
//      line in `server/modes.mjs` naming it.
//
// Pages, the welcome, Settings, Choose lines, the board switch, the planner's
// badges, the tab label, push titles, severity and the entry-chunk guard all
// read this file and need no change.

/** The words every opt-in mode must supply; a declaration missing one is a bug. */
const REQUIRED_LABELS = ['one', 'many', 'line', 'lines', 'stop', 'stops', 'tag', 'group'];

/** Fill in what a declaration may leave to the pattern. */
export function defineMode(def) {
  const id = String(def.id);
  const source = def.source === 'network' ? 'network' : 'adapter';
  const optIn = def.optIn === true;
  // City in the key, so two cities' modes of the same id never share a switch.
  const keyOf = (what) => `lateagain:mode:${def.city}:${id}:${what}:v1`;
  return {
    api: source === 'adapter' ? `/api/modes/${id}` : null,
    band: null,
    data: {},
    badge: { style: source === 'network' ? 'bar' : 'number', colour: '#909194' },
    severity: { rules: 'rail', alertMinutes: 0 },
    planner: null,
    stopFacts: [],
    // How far ahead a stop's board looks, in minutes. The server cuts the board
    // there and the board's own copy says it, from this one number.
    windowMinutes: 60,
    search: { numberWord: 'stop', noteNumber: 'STOP', noteFallback: 'STOP' },
    credits: '',
    ...def,
    id,
    source,
    optIn,
    // Declared but not shown: see `hidden` in the shape above.
    hidden: def.hidden === true,
    // How a stop's two sides are paired; see `pairing` in the shape above.
    pairing: def.pairing === 'road' ? 'road' : 'number',
    prefs: {
      on: keyOf('on'), intro: keyOf('intro'), stars: keyOf('stars'), opens: keyOf('opens'),
      ...(def.prefs ?? {}),
    },
    welcome: def.welcome ? { step: id, ...def.welcome } : null,
  };
}

// ── Melbourne ───────────────────────────────────────────────────────────────

const MELBOURNE_TRAM = defineMode({
  id: 'tram',
  city: 'melbourne',
  source: 'adapter',
  adapter: 'ptv-tram',
  // Live data is Transport Victoria's GTFS feed now, so copy must not say PTV.
  sourceShort: 'Transport Victoria',
  // Stable on the wire. See `api` above.
  api: '/api/trams',
  // 6000 plus the number on the front of the tram: route 96 is 6096. PTV's own
  // tram route ids would collide with Sydney's train band (route 82 is PTV
  // route 1002, which is Sydney's T2). The full argument is in
  // `src/tram-lines.js`, which now only holds Melbourne's adapter facts.
  band: { base: 6000, max: 199 },
  data: { routes: 'tram-routes.melbourne.json', stops: 'trams.melbourne.json' },
  labels: {
    one: 'tram', many: 'trams',
    line: 'route', lines: 'routes',
    stop: 'stop', stops: 'stops',
    tag: 'TRAM',
    group: 'TRAMS',
    // "station or tram stop": what the search says it is looking for.
    place: 'tram stop',
    // The colour its rail and ticks are drawn in, in words ("Green marks…").
    colour: 'green',
  },
  // PTV's tram green, one colour for all twenty-four routes, for the reason
  // V/Line is one purple: the number on the badge tells two apart.
  badge: { style: 'number', colour: '#78be20' },
  // A tram alert is a diversion, a bus replacement, or a delay over ten
  // minutes. A tram shares the road; two minutes behind a turning truck is not
  // news. See `streetSeverityOf` in severity.js.
  severity: { rules: 'street', alertMinutes: 10 },
  optIn: true,
  // The keys this has always written, so an install migrates by doing nothing.
  prefs: {
    on: 'lateagain:trams:v1',
    intro: 'lateagain:trams-intro:v1',
    stars: 'lateagain:tram-stars:v1',
    opens: 'lateagain:board-opens-tram:v1',
  },
  // The step id is saved by name in `lateagain:welcome-step`, so it stays
  // `trams` and an interrupted welcome resumes where it was.
  welcome: {
    step: 'trams',
    title: 'Any trams?',
    why: 'Add the routes you catch. Short hold-ups on trams stay quiet: you hear '
      + 'about diversions, replacement buses and anything over 10 minutes.',
    near: 'NEAR YOUR STATIONS',
    nearMe: 'NEAR YOU',
    nearMeAsk: 'Show routes near me',
    nearMeNone: 'No tram routes within a short walk of you.',
    search: 'Search a stop or street',
    searchNone: 'No stop found. Try a street, a landmark or a stop number.',
    all: (n) => `ALL ${n} ROUTES`,
    allLabel: 'Every tram route',
    skip: 'No trams',
    // The count as the sentence "2 lines and 1 tram route watched" says it.
    watched: (n) => `${n} tram ${n === 1 ? 'route' : 'routes'}`,
  },
  settings: {
    heading: 'Trams',
    title: 'Melbourne trams',
    covers: ['YARRA TRAMS', '24 ROUTES'],
    note: 'Your tram routes beside your train lines, a board for each tram '
      + 'stop, and trams in trip plans. Short hold-ups on trams stay quiet: '
      + 'you hear about diversions, replacement buses and delays over 10 '
      + 'minutes. Off takes your tram routes off the list.',
  },
  intro: {
    title: 'You can add trams now',
    body: 'Short hold-ups on trams stay quiet. You hear about diversions, replacement '
      + 'buses and anything over 10 minutes.',
    action: 'Add trams',
    sample: '96',
  },
  home: { offer: 'TRAM ROUTES' },
  planner: { key: 'trams', api: '/api/trams/plan', label: 'Trams' },
  windowMinutes: 75,
  stopFacts: [
    { field: 'freeTramZone', label: 'FREE TRAM ZONE', atBothEnds: true, zoneTag: 'FREE ZONE' },
    { field: 'levelAccess', label: 'LEVEL ACCESS' },
    // Only where PTV says there is none, and only when no side has it.
    { field: 'stepUp', label: 'NO LEVEL ACCESS', quiet: true, unless: 'levelAccess' },
  ],
  search: { numberWord: 'stop', noteNumber: 'STOP', noteFallback: 'TRAM STOP' },
  credits: 'Stops and notices from PTV, tram positions from Yarra Trams through '
    + 'Transport Victoria. Late Again is not affiliated with Yarra Trams.',
});

// ── Melbourne's metropolitan buses ──────────────────────────────────────────
//
// DATA AND SERVER ONLY, AND HIDDEN. The stops, the timetable, the live feed and
// the PTV notices are all here and answer under `/api/buses`; nothing on a phone
// asks, and `hidden` keeps the registry's UI readers from offering it. See
// `hidden` in the shape above. Declared exactly as the trams are: a band, an
// adapter, labels, a badge, the street rulebook, opt-in.
//
// The rest of what a person sees (the welcome step, the Settings row, the
// one-time card, the planner switch) is the UI phase's and is deliberately not
// written: a declaration with copy nobody has reviewed is copy waiting to ship.

const MELBOURNE_BUS = defineMode({
  id: 'bus',
  city: 'melbourne',
  source: 'adapter',
  adapter: 'ptv-bus',
  api: '/api/buses',
  // 7000 plus the number on the front of the bus: route 200 is 7200. Free of
  // the trains (1–5999) and the trams (6001–6199), and of every notice-id base.
  // The metropolitan network's numbers run 150–982, so 999 leaves room; the few
  // routes whose name is not a plain number ("140A", "695F", the Anzac Day
  // ANZAC_n) have no id and are left out, and the build names them.
  band: { base: 7000, max: 999 },
  data: { routes: 'bus-routes.melbourne.json', stops: 'buses.melbourne.json' },
  labels: {
    one: 'bus', many: 'buses',
    line: 'route', lines: 'routes',
    stop: 'stop', stops: 'stops',
    tag: 'BUS',
    group: 'BUSES',
    place: 'bus stop',
    colour: 'orange',
  },
  // PTV's bus orange, one colour for all of them, for the reason the trams are
  // one green. The orange is light: the number on it is drawn dark.
  badge: { style: 'number', colour: '#ff8200', text: '#1a1a1a' },
  // The same rulebook as the trams, and for the same reason: a bus shares the
  // road. A diversion, a replacement, or a delay over ten minutes alerts.
  // `replaceable: false` keeps "buses" out of what a replacement bus is said to
  // replace (severity.js): a train notice that says "no buses" is not a missing
  // bus, and "buses replace buses" is not a thing.
  severity: { rules: 'street', alertMinutes: 10, replaceable: false },
  optIn: true,
  hidden: false,
  // The words of the opt-in surfaces, written as the trams' are. Both modes
  // alert on the same street rules, so the copy says the same thing in its own
  // nouns.
  welcome: {
    title: 'Any buses?',
    why: 'Add the routes you catch. Short hold-ups on buses stay quiet: you hear '
      + 'about diversions and anything over 10 minutes.',
    near: 'NEAR YOUR STATIONS',
    nearMe: 'NEAR YOU',
    nearMeAsk: 'Show routes near me',
    nearMeNone: 'No bus routes within a short walk of you.',
    search: 'Search a route, stop or street',
    searchNone: 'No stop found. Try a street, a landmark or a suburb.',
    all: (n) => `ALL ${n} ROUTES`,
    allLabel: 'Every bus route',
    skip: 'No buses',
    watched: (n) => `${n} bus ${n === 1 ? 'route' : 'routes'}`,
  },
  settings: {
    heading: 'Buses',
    title: 'Melbourne buses',
    covers: ['METRO BUSES', '371 ROUTES'],
    note: 'Your bus routes beside your train lines and trams, a board for each '
      + 'bus stop, and buses in trip plans. Short hold-ups on buses stay quiet: '
      + 'you hear about diversions and delays over 10 minutes. Off takes your '
      + 'bus routes off the list.',
  },
  intro: {
    title: 'You can add buses now',
    body: 'Short hold-ups on buses stay quiet. You hear about diversions and '
      + 'anything over 10 minutes.',
    action: 'Add buses',
    sample: '246',
  },
  home: { offer: 'BUS ROUTES' },
  // The bus stop's sign carries no number the rider reads, so the poles of one
  // stop are paired by the road they stand on instead. See `src/stop-index.js`.
  pairing: 'road',
  // Plans with trains and with one change of bus (server/street-plan.mjs). The
  // screens that offer it read this and the hidden flag, so it shows nothing yet.
  planner: { key: 'buses', api: '/api/buses/plan', label: 'Buses' },
  windowMinutes: 75,
  search: { numberWord: 'stop', noteNumber: 'STOP', noteFallback: 'BUS STOP' },
  credits: 'Stops and notices from PTV, bus positions from the operators through '
    + 'Transport Victoria.',
});

// ── Light rail inside the train bundles ─────────────────────────────────────
//
// SYDNEY'S L1–L4, GOLD COAST G:LINK, ADELAIDE'S GLENELG TRAM, CANBERRA'S LIGHT
// RAIL. Their lines are in the same GTFS bundle as the trains, tagged
// `mode: 'lightrail'` by `scripts/fetch-network-gtfs.mjs`, and their stops are
// stations: the departures board already lists them, so there is no board to
// add, nothing to opt in to and no data to load. They are declared so the
// generic screens can name them (Choose lines groups them under LIGHT RAIL)
// and so that moving them onto the rest of the model is a change of
// declaration rather than a rewrite. They are JUDGED BY THE TRAIN RULES, as
// they always have been; see the PR for what moving that is a decision about.

const lightRail = (city) => defineMode({
  id: 'lightrail',
  city,
  source: 'network',
  lineMode: 'lightrail',
  labels: {
    one: 'light rail', many: 'light rail',
    line: 'line', lines: 'lines',
    stop: 'stop', stops: 'stops',
    tag: 'LIGHT RAIL',
    group: 'LIGHT RAIL',
  },
  badge: { style: 'bar', colour: null },
  severity: { rules: 'rail', alertMinutes: 0 },
  optIn: false,
});

/** Every mode every city declares, by city then mode id. */
export const CITY_MODES = {
  melbourne: { tram: MELBOURNE_TRAM, bus: MELBOURNE_BUS },
  sydney: { lightrail: lightRail('sydney') },
  brisbane: { lightrail: lightRail('brisbane') },
  adelaide: { lightrail: lightRail('adelaide') },
  canberra: { lightrail: lightRail('canberra') },
};

// ── Reading the registry ────────────────────────────────────────────────────
//
// Every reader goes through `registry()` rather than `CITY_MODES`, so a test can
// put a different one in place with `useModeRegistry` and show that nothing
// below knows what a tram is. Tests only; the app never calls it.

let active = CITY_MODES;

/** Tests only: read this registry instead of the committed one, or null to go back. */
export function useModeRegistry(registryOrNull) {
  active = registryOrNull ?? CITY_MODES;
}

/** The registry in force. */
export const registry = () => active;

/** Every declared mode, across every city. */
export function allModes() {
  return Object.values(active).flatMap((byId) => Object.values(byId));
}

/**
 * The modes a city SHOWS, in declaration order: what the screens walk. A hidden
 * mode is not among them; ask for it with `{ hidden: true }` (the server and the
 * build scripts), or by id with `modeOf`.
 */
export function modesOf(city, { hidden = false } = {}) {
  return Object.values(active[String(city)] ?? {}).filter((m) => hidden || !m.hidden || isServed(m));
}

/** One mode by city and id, or null. Hidden modes included: an id names it outright. */
export function modeOf(city, id) {
  return active[String(city)]?.[String(id)] ?? null;
}

/** The modes with a source of their own: stops, boards and a feed. Shown ones only. */
export const adapterModes = (city) => modesOf(city).filter((m) => m.source === 'adapter');

/** The modes a person turns on. Shown ones only. */
export const optInModes = (city) => modesOf(city).filter((m) => m.optIn);

/** Every adapter mode in every city, hidden or not: what the build scripts and line-id classification walk. */
export const everyAdapterMode = () => allModes().filter((m) => m.source === 'adapter');

/**
 * Is this mode live where this code runs? A hidden mode is not, unless
 * LATE_AGAIN_HIDDEN_MODES names it (comma list, e.g. `bus`): the server must not
 * answer its API, sweep its notices, load its adapter (and start its multi-GB
 * schedule refresh) or take a subscription's ids as a reason to, and the router
 * must not register its screens. The flag exists for local development.
 */
export function isServed(mode) {
  if (!mode) return false;
  if (!mode.hidden) return true;
  // The browser has no `process.env`: `vite.config.js` bakes the flag in as
  // `__LATE_AGAIN_HIDDEN_MODES__` for a dev server started with it, and a
  // production build leaves it empty, so the screens stay off for everyone.
  const baked = typeof __LATE_AGAIN_HIDDEN_MODES__ === 'string' ? __LATE_AGAIN_HIDDEN_MODES__ : '';
  const flag = globalThis.process?.env?.LATE_AGAIN_HIDDEN_MODES || baked;
  return String(flag ?? '').split(',').map((x) => x.trim()).includes(mode.id);
}

/** The adapter modes that are live: what the server's routes, sweep and follows walk. */
export const servedAdapterModes = () => everyAdapterMode().filter(isServed);

// ── Line ids ────────────────────────────────────────────────────────────────

const inBand = (m, n) => m.band && Number.isInteger(n) && n > m.band.base && n <= m.band.base + m.band.max;

/**
 * The adapter mode a line id belongs to, or null for a train line. City-blind
 * on purpose: bands never overlap, so the id alone says (see `assertBands`).
 * Accepts strings, as stored rows carry them.
 */
export function modeOfLine(id) {
  const n = Number(id);
  if (!Number.isInteger(n)) return null;
  for (const m of allModes()) if (inBand(m, n)) return m;
  return null;
}

/** Is this line id one of this mode's? */
export function isModeLine(modeId, id) {
  return modeOfLine(id)?.id === String(modeId);
}

/**
 * The adapter mode a PLANNED LEG is a ride on, or null.
 *
 * A leg names its mode as its `kind` (`'tram'`), and the line it rides is one of
 * that mode's. Both, because a kind alone is ambiguous: the train planner marks a
 * replacement bus `kind: 'bus'`, and `bus` is also the id of a mode. A
 * replacement bus rides a train line (it stands in for one), so its line is not
 * in the bus band and it is not a ride on the mode.
 */
export function modeOfRide(leg) {
  if (!leg || leg.kind == null || leg.kind === 'walk') return null;
  const mode = modeOfLine(leg.routeId);
  return mode && mode.id === String(leg.kind) ? mode : null;
}

/** Is this line id any adapter mode's — that is, not one of the trains'? */
export const isAdapterLine = (id) => modeOfLine(id) !== null;

/**
 * The line id for a route number ("96" → 6096), or null for one with no plain
 * number or outside the band.
 */
export function lineIdOf(mode, routeNumber) {
  if (!mode?.band) return null;
  const text = String(routeNumber ?? '').trim();
  if (!/^\d{1,3}$/.test(text)) return null;
  const n = Number(text);
  return n >= 1 && n <= mode.band.max ? mode.band.base + n : null;
}

/** The number on the front of the vehicle ("96"), or '' for a line that is not this mode's. */
export function routeNumberOf(mode, id) {
  const n = Number(id);
  return inBand(mode, n) ? String(n - mode.band.base) : '';
}

/**
 * The mode a row is about, or null for a train. A server row names its line
 * `routeId`, a stored row `line`, and either may say `mode` outright. `city`
 * narrows a bare `mode` word to that city's declaration.
 */
export function modeOfRow(row, city = null) {
  if (!row) return null;
  const byLine = modeOfLine(row.line ?? row.routeId);
  if (byLine) return byLine;
  const word = row.mode;
  if (!word) return null;
  const mine = city ? modeOf(city, word) : null;
  if (mine) return mine.source === 'adapter' ? mine : null;
  return everyAdapterMode().find((m) => m.id === word) ?? null;
}

/** Is this row about the given adapter mode? */
export const isModeRow = (modeId, row) => modeOfRow(row)?.id === String(modeId);

/** Does any adapter mode claim this row? */
export const isAdapterRow = (row) => modeOfRow(row) !== null;

/** A stop's number as a sign would print it: '' for none, and for PTV's placeholder 0. */
export function realStopNumber(number) {
  const n = String(number ?? '').trim();
  return n === '0' ? '' : n;
}

/** A stop as a ride names it: "Stop 45 · Glen Huntly Rd". Number first. */
export function stopLabelOf(mode, number, name) {
  // PTV prints "0" for the few stops that have no number on the pole: there is
  // no "Stop 0" to say.
  const n = realStopNumber(number);
  const words = String(name ?? '').trim();
  if (!n) return words;
  const word = String(mode?.labels?.stop ?? 'stop');
  const head = `${word.charAt(0).toUpperCase()}${word.slice(1)} ${n}`;
  return words ? `${head} · ${words}` : head;
}

// ── Words ───────────────────────────────────────────────────────────────────

/** "Trams" from "trams": a heading's first letter. */
export const capital = (word) => `${String(word).charAt(0).toUpperCase()}${String(word).slice(1)}`;

/**
 * A regex source matching the vehicle words of every street-rule mode
 * (`trams?`, `buses`): how severity.js tells "Buses replace trams" from
 * "Buses replace trains" without the word in its source.
 *
 * `replaceable: true` keeps only the modes a replacement bus can stand in for.
 * The word is also read as "no <vehicle>" and "<vehicle> do not run", which is
 * right for a tram and wrong for a bus: a TRAIN notice that says "no buses are
 * running" or "replacement buses will not operate" is not a missing train. A
 * mode opts out with `severity.replaceable: false`.
 */
export function vehicleWords({ replaceable = false } = {}) {
  const words = new Set();
  for (const m of everyAdapterMode()) {
    if (m.severity.rules !== 'street') continue;
    if (replaceable && m.severity.replaceable === false) continue;
    words.add(m.labels.one);
    words.add(m.labels.many);
  }
  return [...words].map((w) => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).sort((a, b) => b.length - a.length);
}

// ── What each city shows and what the person has chosen ─────────────────────

/** The steps the welcome asks for modes, in registry order, each once. */
export function welcomeModeSteps() {
  const seen = new Set();
  const out = [];
  for (const m of allModes()) {
    if ((m.hidden && !isServed(m)) || !m.optIn || !m.welcome || seen.has(m.welcome.step)) continue;
    seen.add(m.welcome.step);
    out.push(m.welcome.step);
  }
  return out;
}

/** The mode a welcome step id asks about, within a city, or null. */
export function modeForStep(city, step) {
  return optInModes(city).find((m) => m.welcome?.step === step) ?? null;
}

/**
 * How many of a city's modes have a board of their own on the phone: the
 * departures tab reads BOARD (not TRAINS) once this is more than zero.
 * `on` is the set of mode ids the person has turned on.
 */
export function boardModes(city, on) {
  const set = on instanceof Set ? on : new Set(on ?? []);
  return adapterModes(city).filter((m) => !m.optIn || set.has(m.id));
}

// ── The check ───────────────────────────────────────────────────────────────

/**
 * Throws if any two bands overlap, a band reaches into `reserved` (the id
 * ranges the cities own: pass `[[1, 5999]]` for the train networks), or a
 * declaration is missing a word an opt-in surface needs. Run on every build and
 * by the suite: the failure it prevents is silent — a Melbourne rider woken by
 * somebody else's notice.
 */
export function assertBands({ reserved = [] } = {}) {
  const seen = [];
  for (const m of everyAdapterMode()) {
    if (!m.band) throw new Error(`mode ${m.city}/${m.id} is an adapter mode with no band`);
    const lo = m.band.base + 1;
    const hi = m.band.base + m.band.max;
    for (const [rlo, rhi] of reserved) {
      if (lo <= rhi && hi >= rlo) throw new Error(`mode ${m.city}/${m.id} (${lo}–${hi}) overlaps reserved ids ${rlo}–${rhi}`);
    }
    for (const o of seen) {
      if (lo <= o.hi && hi >= o.lo) throw new Error(`mode ${m.city}/${m.id} (${lo}–${hi}) overlaps ${o.name} (${o.lo}–${o.hi})`);
    }
    seen.push({ name: `${m.city}/${m.id}`, lo, hi });
  }
  for (const m of allModes()) {
    const missing = REQUIRED_LABELS.filter((k) => !m.labels?.[k]);
    if (missing.length) throw new Error(`mode ${m.city}/${m.id} has no label for ${missing.join(', ')}`);
  }
  return seen;
}
